/**
 * The ONE reading of a cleanup answer, for both cleanup dialogs (D32).
 *
 * ── Why this exists ───────────────────────────────────────────────────
 *
 * The single-member dialog and the several-members dialog answer the same
 * questions — what would go, is that answer complete, may Confirm act, and
 * what does the done screen owe the admin — and each answered them on its
 * own. Five times one was fixed and the other was not (D32's table), the
 * last of them the bulk dialog reporting a booking's incompleteness from
 * the PREVIEW it had shown minutes earlier, not from what the booking
 * answered (issues-to-file #1). Since D35 there is no run-now special
 * case left to answer, so what remains fits here, and both dialogs only
 * draw it.
 */
import type { ChatStorageCleanupResult, CleanupActivityRow } from '../../client/storage'

/** What one preview says about one target. */
export type PreviewReading =
  /** The request failed: nothing is known about this target. */
  | { kind: 'failed' }
  /** Zero found, but a workspace could not be read — NOT a proven zero. */
  | { kind: 'unproven'; unreadable: number }
  /** A measured zero: nothing of theirs is idle that long. */
  | { kind: 'nothing'; truncated: boolean }
  /** Some found, but a workspace could not be read: the list may be short. */
  | { kind: 'partial'; conversations: number; bytes: number; unreadable: number; truncated: boolean }
  /** Some found, and every workspace was read. */
  | { kind: 'some'; conversations: number; bytes: number; truncated: boolean }

/**
 * Read a preview. `null` while it has not answered.
 *
 * ⚠️ `workspacesSkipped` is the server's UNREADABLE count (a tenant with no
 * agent data is a proven empty, not a gap), so any non-zero value makes a
 * zero unprovable and a count incomplete.
 */
export function readPreview(r: ChatStorageCleanupResult | null | undefined, failed = false): PreviewReading | null {
  if (failed) return { kind: 'failed' }
  if (!r) return null
  const unreadable = r.workspacesSkipped ?? 0
  const truncated = r.truncated === true
  if (r.conversations === 0) {
    return unreadable > 0 ? { kind: 'unproven', unreadable } : { kind: 'nothing', truncated }
  }
  return unreadable > 0
    ? { kind: 'partial', conversations: r.conversations, bytes: r.bytes, unreadable, truncated }
    : { kind: 'some', conversations: r.conversations, bytes: r.bytes, truncated }
}

/**
 * Whether Confirm should BOOK this target.
 *
 * Only a MEASURED zero is skipped: booking it would tell the member their
 * conversations are about to be cleared and then clear nothing. Every
 * other answer is booked — an unproven zero and a failed preview included,
 * because then nobody has shown that they have nothing, and the server's
 * own refusals (nothing to do / could not read) decide instead of a guess
 * made here.
 */
export function worthBooking(p: PreviewReading | null): boolean {
  return p !== null && p.kind !== 'nothing'
}

/**
 * Whether the SINGLE-member dialog's Confirm is armed.
 *
 * Stricter than `worthBooking` in one way only: that dialog writes the
 * count on its button ("Clear 312 conversations now"), so it needs an
 * answer with a count — a failed preview leaves it disarmed, with the
 * preview box saying it could not check. The bulk dialog has no count on
 * its button and books a failed member (see `worthBooking`).
 */
export function singleConfirmArmed(p: PreviewReading | null): boolean {
  return p !== null && p.kind !== 'failed' && worthBooking(p)
}

/** What a BOOKING's own answer says the admin must be told. */
export interface BookingNotes {
  /** Booked from an incomplete scan: the frozen list may be short. */
  partial: boolean
  /** The list hit the most one booking can freeze: more is past the
   *  window, for another booking once this one is done. */
  truncated: boolean
}

/**
 * Read a booking's answer — never the preview that came before it. The
 * booking freezes its own list with its own scan, minutes later; a
 * workspace unreadable then (or readable again) is what the frozen list
 * actually reflects (issues-to-file #1).
 */
export function readBooking(r: ChatStorageCleanupResult): BookingNotes {
  return {
    partial: (r.workspacesSkipped ?? 0) > 0,
    truncated: r.truncated === true,
  }
}

/**
 * Is this booking UNDER WAY? The server's `cleanupUnderWay` (Season
 * 2026-10-22, option A), read on the PAGE: running, between passes, or
 * its time has come and no worker has taken it yet.
 *
 * ⚠️ THE CLOCK, TOO. The server answers when the page loads; a page left
 * open across a booking's `runAt` must turn it "running" when the time
 * comes, not on the next reload — else it offers Cancel for what the
 * server will answer as a STOP (scoped review of round 7, #2). The section
 * re-renders at the soonest `runAt` (`nextFlipAt`) and passes `nowMs`.
 * One reading for the dialog's label AND its polling (#1 there: they used
 * two rules, and a row shown RUNNING was never polled to DONE).
 */
export function activityUnderWay(
  r: Pick<CleanupActivityRow, 'state' | 'runAt' | 'resumed' | 'due'>, nowMs: number = Date.now(),
): boolean {
  if (r.state === 'running') return true
  if (r.state !== 'scheduled') return false
  return r.resumed === true || r.due === true || Date.parse(r.runAt) <= nowMs
}

/*
 * ACCEPTED: the flip uses the BROWSER's clock; the server judges "due" by
 * the API's. Under a skew of X the row turns X early or late — a Cancel
 * pressed in that window comes back "stopped" (or a Stop "cancelled"), and
 * the outcome line says which, so nothing is hidden. Before the flip timer
 * the window lasted until a reload (scoped review of round 8, #2).
 */
/** The same for a LIVE booking from the listing (which already says
 *  `running` for one between passes, or due when it was read). */
export function liveUnderWay(cu: { state: 'scheduled' | 'running'; runAt: string }, nowMs: number = Date.now()): boolean {
  return cu.state === 'running' || Date.parse(cu.runAt) <= nowMs
}

/** When the next scheduled booking's time comes (ms epoch), or null. */
export function nextFlipAt(runAts: Iterable<string>, nowMs: number): number | null {
  let soonest: number | null = null
  for (const at of runAts) {
    const t = Date.parse(at)
    if (Number.isFinite(t) && t > nowMs && (soonest === null || t < soonest)) soonest = t
  }
  return soonest
}
